
import { useState, createContext, useEffect, PropsWithChildren, useContext } from 'react'
import Cookies from 'universal-cookie';
import { Navigate } from 'react-router-dom';
import { IUserDetailsContext } from './user-details.model';
import UserDetailsService from '../../shared/services/user-details/user-details.service';
import { IUserDetails } from '../../shared/services/user-details/user-details.model';
import { createTheme,} from '@mui/material';
import gaiaTheme from '../../root-components/theme';
import { deepmerge } from '@mui/utils';
import { lighten } from '@mui/system';
const { palette } = createTheme();

export const UserDetailsContext = createContext<IUserDetailsContext>({} as IUserDetailsContext);

export const UserDetailsProvider = ({ children }: PropsWithChildren) => {

  const cookies = new Cookies(null, { path: '/' });

  const userDetailsService = new UserDetailsService();

  const [userDetails, setUserDetails] = useState<IUserDetails | undefined>(undefined);
  const [change, setChange] = useState(0);
  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false)

  useEffect(() => {
    const cookie = cookies.get("gca_lat");
    if (!userDetails && cookie) {
      userDetailsService.getUserDetails()
        .then(response => {
          let formattedResponse = response
          if ((response?.isPartner && response?.isLoggedInAsClient) || (!response?.organisation?.isPartner && response?.partnerOrganisation)) {
            const partnerTheme = mergePartnerTheme(response);
            formattedResponse = { ...response, organisation: { ...response.organisation, theme: partnerTheme } };
          }
          setUserDetails(formattedResponse);
          setInitialLoadComplete(true);
        })
        .catch(() => {
          setUserDetails(undefined);
          setInitialLoadComplete(true);
          return <Navigate to="/login" replace />;
        })
    } else {
      setUserDetails(undefined);
      setInitialLoadComplete(true);
    }
  }, [change]);

  function forceUpdateUserDetails(): Promise<IUserDetails> {
    return new Promise(function (resolve, reject) {
      userDetailsService.getUserDetails()
        .then(response => {
          let formattedResponse = response
          if ((response?.isPartner && response?.isLoggedInAsClient) || (!response?.organisation?.isPartner && response?.partnerOrganisation)) {
            const partnerTheme = mergePartnerTheme(response);
            formattedResponse = { ...response, organisation: { ...response.organisation, theme: partnerTheme } };
          } else {
            formattedResponse = { ...response, organisation: { ...response.organisation, theme: gaiaTheme } };
          }
          setUserDetails(formattedResponse);
          resolve(formattedResponse);
        })
        .catch(() => {
          setUserDetails(undefined);
          reject();
        })
    })
  }

  function clearUserDetails(): void {
    setUserDetails(undefined);
    cookies.remove("gca_lat");
  };

  const mergePartnerTheme = (response: IUserDetails) => {
    const partnerColor = response?.partnerOrganisation?.partnerColor || response?.organisation?.partnerColor;
    if (partnerColor) {
      const theme = deepmerge(gaiaTheme, createTheme({
        components: {
          MuiButton: {
            defaultProps: {
              disableRipple: true,
              color: 'primary',
              variant: 'contained',
              disableElevation: true,
            },
            styleOverrides: {
              root: {
                textTransform: 'none',
              },
            },
            variants: [
              {
                props: { variant: 'text' },
                style: {
                  backgroundColor: 'white',
                  color: 'primary.main'
                }
              }
            ]
          },
          MuiTab: {
            defaultProps: {
              disableRipple: true,
            },
            styleOverrides: {
              root: {
                textTransform: 'capitalize',
              }
            }
          }
        },
        typography: {
          fontFamily: [
            'EffraMd',
            'EffraRg',
            'Effra',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif'
          ].join(','),
          body1: {
            fontFamily: [
              'EffraRg',
              'EffraMd',
              'Effra',
              'Roboto',
              '"Helvetica Neue"',
              'Arial',
              'sans-serif'
            ].join(',')
          }
        },
        palette: {
          primary: {
            main: partnerColor,
            contrastText: "#fff"
          },
          secondary: {
            main: lighten(partnerColor, 0.6),
          },
          lightGrey: palette.augmentColor({
            color: {
              main: '#e7e7e7',
              light: '#f1f1f1',
              dark: '#0000001f'
            }
          }),
          text: {
            primary: "#111E21"
          },
          error: {
            main: '#f44336',
            light: '#f6685e',
            dark: '#aa2e25',
          },
          warning: {
            main: '#ff9800',
            light: '#ffb74d',
            dark: '#f57c00',
            contrastText: '#fff',
          },
        },
        shape: {
          borderRadius: 10
        }
      }));
      return theme;
    } else {
      return gaiaTheme;
    }
  }
  return (
    // <ThemeProvider theme={{ ...theme }}>
      <UserDetailsContext.Provider value={{ userDetails, setChange, forceUpdateUserDetails, clearUserDetails }}>
        {initialLoadComplete && children}
      </UserDetailsContext.Provider>
    // </ThemeProvider>
  )
}